import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    ModalHeader,
    ModalFooter,
    TabNavigation,
    Button,
    ModalBody,
    Modal
} from 'common/components';
import { ApiStatus, ButtonVariant, IndicatorDetailFields } from 'common/enums';
import { FieldError } from 'common/models';
import {
    ACTION_ITEMS,
    INDICATOR_NAME_EMPTY_ERROR,
    INDICATOR_NAME_INVALID_CHAR_ERROR,
    INDICATOR_NAME_MAX_LENGTH,
    INDICATOR_NAME_MAX_LENGTH_ERROR,
    INDICATOR_TAB_VALUES,
    INDICATOR_ORDER_EMPTY_ERROR,
    INDICATOR_DESCRIPTION_MAX_LENGTH,
    INDICATOR_DESCRIPTION_MAX_LENGTH_ERROR,
    INDICATOR_DESCRIPTION_INVALID_CHAR_ERROR,
    INDICATOR_REFERENCE_MAX_LENGTH,
    INDICATOR_REFERENCE_MAX_LENGTH_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH,
    INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_INTEGER_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH,
    INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH_ERROR,
    INDICATOR_NAME_REQUIRED,
    INDICATOR_ORDER_GREATER_THAN_ZERO,
    INDICATOR_ORDER_INVALID_ERROR,
    INDICATOR_ORDER_MAX_LENGTH,
    INDICATOR_ORDER_MAX_LENGTH_ERROR,
    INDICATOR_ORDER_REQUIRED,
    INDICATOR_SOURCE_DATE_ERROR,
    INDICATOR_SOURCE_DATE_FORMAT_ERROR,
    INDICATOR_SOURCE_REQUIRED,
    INDICATOR_UPDATE_FREQUENCY_REQUIRED,
    INDICATOR_WEB_ADDRESS_REQUIRED
} from 'common/config';
import { addValidationError, removeValidationError } from 'common/utils/error';

import IndicatorDetails from '../edit-indicator-details/IndicatorDetails';
import IndicatorCountryList from '../edit-indicator-country-list/IndicatorCountryList';
import { RootState } from 'common/redux/core/root.reducer';
import {
    IndicatorDTO,
    IndicatorLocation,
    IndicatorLocationTemp,
    IndicatorUpdateDTO
} from 'common/models/data/indicator.model';
import { hasNoValue, hasValue } from 'common/utils/data';
import {
    validateDecimalNumbers,
    validateInputString,
    validateMaxLength,
    validateNumbers
} from 'common/utils/validator';
import { EMPTY_GUID } from 'common/constants/user';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { getIndicatorDetailsForEdit, getSourceList } from 'common/redux/entity/entity.selector';
import { entityActions } from 'common/redux/entity';
import { removeTimeOffset } from 'common/utils/date';
import useIndicatorSourceAccess from 'modules/configuration/roles-access-hooks/useIndicatorSourceAccess';

import './EditIndicator.scss';

type EditIndicatorProps = {
    data: EntityDetailsPayload;
    fieldErrors: Array<FieldError>;
    onClose: () => void;
    onUpdate: (updatedData: IndicatorUpdateDTO) => void;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    isEditModalVisible: boolean;
};

const EditIndicator: FC<EditIndicatorProps> = ({
    data,
    fieldErrors,
    isEditModalVisible,
    onClose,
    updateFieldErrors,
    onUpdate
}: EditIndicatorProps) => {
    const [tab, setTab] = useState<string>(INDICATOR_TAB_VALUES[0]);
    const [indicatorModel, setIndicatorModel] = useState<IndicatorDTO>({});
    const [indicatorCountryData, setIndicatorCountryData] = useState<IndicatorLocationTemp[]>();
    const [isShowUpdateTypeAlert, setShowUpdateTypeAlert] = useState<boolean>(false);
    const [isOverride, setOverride] = useState<boolean>(false);
    const [dataCount, setDataCount] = useState<number>(0);
    const [sourceSearchValue, setSourceSearchValue] = useState<string>('');

    const indicator = useSelector((state: RootState) => getIndicatorDetailsForEdit(state));
    const sourceList = useSelector((state: RootState) => getSourceList(state));
    const { isSetUpDisabled } = useIndicatorSourceAccess();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(entityActions.fetchSources(data.initiator));
    }, [data.initiator, dispatch]);

    useEffect(() => {
        dispatch(entityActions.fetchIndicatorDetailsForEdit(data));
    }, [data, dispatch]);

    useEffect(() => {
        const tempIndicatorDTO: IndicatorDTO = {
            description: !hasNoValue(indicator?.data?.details.description)
                ? indicator?.data?.details.description
                : '',
            name: !hasNoValue(indicator?.data?.details.name) ? indicator?.data?.details.name : '',
            order: !hasNoValue(indicator?.data?.details.order)
                ? indicator?.data?.details.order
                : '',
            parentCategoryId: indicator?.data?.details.parentCategoryId,
            reference: !hasNoValue(indicator?.data?.details.reference)
                ? indicator?.data?.details.reference
                : '',
            sortOrder: !hasNoValue(indicator?.data?.details.sortOrder)
                ? indicator?.data?.details.sortOrder
                : '',
            sourceId: !hasNoValue(indicator?.data?.details.sourceId)
                ? indicator?.data?.details.sourceId
                : '',
            frequencyTypeId: !hasNoValue(indicator?.data?.details.frequencyTypeId)
                ? indicator?.data?.details.frequencyTypeId
                : '',
            valueType: !hasNoValue(indicator?.data?.details.valueType)
                ? indicator?.data?.details.valueType
                : '',
            webAddress: !hasNoValue(indicator?.data?.details.sourceId)
                ? indicator?.data?.details.sourceId
                : '',
            sourceDate: !hasNoValue(indicator?.data?.details.sourceDate)
                ? new Date(
                      indicator?.data?.details?.sourceDate
                          ? indicator?.data?.details?.sourceDate
                          : ''
                  )
                : undefined
        };

        if (sourceList?.data) {
            const sourceName = sourceList?.data?.find(
                (item: any) => item.sourceId === tempIndicatorDTO.sourceId
            )?.sourceName;
            setSourceSearchValue(sourceName || '');
        }

        const roundOffIfInteger = (value: string) => {
            if (
                indicator?.data?.details.valueType === 'Integer' &&
                value &&
                !isNaN(Number(value))
            ) {
                return Number(value).toFixed(0);
            }
            return value;
        };

        setIndicatorModel(tempIndicatorDTO);
        const indicatorCountryList: IndicatorLocationTemp[] = [];
        if (indicator?.data?.locations) {
            indicator?.data.locations.forEach((loc) => {
                indicatorCountryList.push({
                    locationId: loc.locationId,
                    locationName: loc.locationName,
                    value: roundOffIfInteger(loc.value),
                    tempValue: roundOffIfInteger(loc.value),
                    indicatorId: data.id,
                    locationDetailsId: loc.locationDetailsId,
                    hasError: loc.hasError
                });
            });
            setDataCount(
                indicatorCountryList.filter((loc) => !hasNoValue(loc.value) && loc.value !== '')
                    .length
            );
        }
        setIndicatorCountryData(indicatorCountryList);
    }, [data.id, indicator, sourceList?.data]);

    const onTabClick = (tab: string) => {
        setTab(tab);
    };

    const handleIndicatorValueChange = (countryName: string, value: string) => {
        let errors = removeValidationError(fieldErrors, countryName);

        const newCountryData = indicatorCountryData?.map((item: IndicatorLocationTemp) => {
            if (item.locationName === countryName) {
                if (indicatorModel.valueType == 'Decimal') {
                    if (!validateDecimalNumbers(value)) {
                        errors = addValidationError(
                            errors,
                            item.locationName,
                            INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_ERROR
                        );
                    } else if (Number(value) > INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH) {
                        errors = addValidationError(
                            errors,
                            item.locationName,
                            INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH_ERROR
                        );
                    }
                    return {
                        ...item,
                        value
                    };
                } else if (indicatorModel.valueType == 'Integer') {
                    if (!validateNumbers(value)) {
                        errors = addValidationError(
                            errors,
                            item.locationName,
                            INDICATOR_COUNTRY_VALUE_TYPE_INTEGER_ERROR
                        );
                    } else if (Number(value) > INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH) {
                        errors = addValidationError(
                            errors,
                            item.locationName,
                            INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH_ERROR
                        );
                    }
                    return {
                        ...item,
                        value
                    };
                } else {
                    if (validateMaxLength(value, INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH)) {
                        errors = addValidationError(
                            errors,
                            item.locationName,
                            INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH_ERROR
                        );
                    }
                    return {
                        ...item,
                        value
                    };
                }
            }
            return item;
        });
        setIndicatorCountryData(newCountryData);
        if (newCountryData) {
            setDataCount(
                newCountryData.filter((loc) => !hasNoValue(loc.value) && loc.value !== '').length
            );
        }
        updateFieldErrors(errors);
    };

    const handleChange = (field: string, value: any) => {
        let errors = removeValidationError(fieldErrors, field);

        if (field === IndicatorDetailFields.Name) {
            if (validateMaxLength(value, INDICATOR_NAME_MAX_LENGTH)) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Name,
                    INDICATOR_NAME_MAX_LENGTH_ERROR
                );
            }
            if (value.trim().length === 0) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Name,
                    INDICATOR_NAME_REQUIRED
                );
            } else if (!validateInputString(value)) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Name,
                    INDICATOR_NAME_INVALID_CHAR_ERROR
                );
            }
        } else if (field === IndicatorDetailFields.Description) {
            if (validateMaxLength(value, INDICATOR_DESCRIPTION_MAX_LENGTH)) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Description,
                    INDICATOR_DESCRIPTION_MAX_LENGTH_ERROR
                );
            }
            if (!validateInputString(value)) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Description,
                    INDICATOR_DESCRIPTION_INVALID_CHAR_ERROR
                );
            }
        } else if (field === IndicatorDetailFields.Reference) {
            if (validateMaxLength(value, INDICATOR_REFERENCE_MAX_LENGTH)) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Reference,
                    INDICATOR_REFERENCE_MAX_LENGTH_ERROR
                );
            }
        } else if (field === IndicatorDetailFields.SourceDate) {
            // if (value !== '' && !validateMonthYear(value)) {

            if (value == '' || value == null) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.SourceDate,
                    INDICATOR_SOURCE_DATE_FORMAT_ERROR
                );
            }
        } else if (field === IndicatorDetailFields.Order) {
            if (value !== '') {
                if (!validateNumbers(value)) {
                    errors = addValidationError(
                        errors,
                        IndicatorDetailFields.Order,
                        INDICATOR_ORDER_INVALID_ERROR
                    );
                } else {
                    if (Number(value) === 0) {
                        errors = addValidationError(
                            errors,
                            IndicatorDetailFields.Order,
                            INDICATOR_ORDER_GREATER_THAN_ZERO
                        );
                    } else if (Number(value) > INDICATOR_ORDER_MAX_LENGTH) {
                        errors = addValidationError(
                            errors,
                            IndicatorDetailFields.Order,
                            INDICATOR_ORDER_MAX_LENGTH_ERROR
                        );
                    }
                }
            } else {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Order,
                    INDICATOR_ORDER_REQUIRED
                );
            }
        } else if (field === IndicatorDetailFields.FrequencyTypeId) {
            if (value == '' || value == null) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.FrequencyTypeId,
                    INDICATOR_UPDATE_FREQUENCY_REQUIRED
                );
            }
        }
        if (field === IndicatorDetailFields.WebAddress) {
            setIndicatorModel({ ...indicatorModel, [IndicatorDetailFields.SourceId]: value });
        } else setIndicatorModel({ ...indicatorModel, [field]: value });

        updateFieldErrors(errors);
    };

    const handleSave = () => {
        let errors = fieldErrors;
        if (!indicatorModel[IndicatorDetailFields.Name]?.trim()) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Name,
                INDICATOR_NAME_EMPTY_ERROR
            );
        }

        if (!indicatorModel[IndicatorDetailFields.Order]) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Order,
                INDICATOR_ORDER_EMPTY_ERROR
            );
        }

        if (
            !indicatorModel[IndicatorDetailFields.SourceId] ||
            indicatorModel[IndicatorDetailFields.SourceId] === EMPTY_GUID
        ) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.WebAddress,
                INDICATOR_WEB_ADDRESS_REQUIRED
            );
        }

        if (!sourceSearchValue.trim()) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.SourceId,
                INDICATOR_SOURCE_REQUIRED
            );
        }

        if (
            indicatorModel[IndicatorDetailFields.SourceDate] == '' ||
            indicatorModel[IndicatorDetailFields.SourceDate] == null
        ) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.SourceDate,
                INDICATOR_SOURCE_DATE_ERROR
            );
        }

        if (!indicatorModel[IndicatorDetailFields.FrequencyTypeId]) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.FrequencyTypeId,
                INDICATOR_UPDATE_FREQUENCY_REQUIRED
            );
        }

        const indicatorCountries: IndicatorLocation[] = [];
        indicatorCountryData?.forEach((icd) => {
            if (icd.value !== icd.tempValue)
                indicatorCountries.push({
                    indicatorId: icd.indicatorId,
                    locationDetailsId: icd.locationDetailsId,
                    locationId: icd.locationId,
                    locationName: icd.locationName,
                    value:
                        indicatorModel.valueType == 'Decimal'
                            ? icd.value !== ''
                                ? parseFloat(icd.value).toFixed(2)
                                : ''
                            : icd.value
                });
        });

        if (errors.length === 0) {
            const updatedData: IndicatorUpdateDTO = {
                ...data,
                ...indicatorModel,
                indicatorId: data.id,
                valueType: hasValue(indicatorModel.valueType) ? indicatorModel.valueType : '',
                indicatorData: indicatorCountries,
                activeStatus: 1,
                sourceDate: indicatorModel.sourceDate
                    ? removeTimeOffset(indicatorModel.sourceDate)
                    : indicatorModel.sourceDate
            };
            onUpdate(updatedData);
        }
        updateFieldErrors(errors);
    };

    const handleSourceSearchChange = (value: string) => {
        setSourceSearchValue(value);
        let errors = removeValidationError(fieldErrors, IndicatorDetailFields.SourceId);
        if (value.trim().length === 0) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.SourceId,
                INDICATOR_SOURCE_REQUIRED
            );
            errors = addValidationError(
                errors,
                IndicatorDetailFields.WebAddress,
                INDICATOR_WEB_ADDRESS_REQUIRED
            );
            setIndicatorModel({ ...indicatorModel, [IndicatorDetailFields.SourceId]: '' });
        }
        updateFieldErrors(errors);
    };

    const handleSourceSelection = (value: string) => {
        setSourceSearchValue(value);
        const errors = removeValidationError(fieldErrors, IndicatorDetailFields.SourceId);
        updateFieldErrors(errors);
    };

    const headerElement = (
        <header className="indicator-edit-header-name-wrapper">
            Editing Indicator:{' '}
            <span className="indicator-edit-header-name" title={indicator?.data?.details.name}>
                {indicator?.data?.details.name ? indicator.data.details.name : ''}
            </span>
        </header>
    );

    const isDataLoading =
        indicator?.status === ApiStatus.LOADING || sourceList?.status === ApiStatus.LOADING;

    return (
        <Modal
            show={isEditModalVisible && !isDataLoading}
            onClose={onClose}
            style={{ width: '65vw', height: '84vh' }}
            className="edit-indicator-modal">
            <ModalHeader className="indicator-edit-header">{headerElement}</ModalHeader>
            <ModalBody className="indicator-edit-body">
                <TabNavigation
                    tabs={INDICATOR_TAB_VALUES}
                    className="tab-navigation"
                    onTabClick={onTabClick}
                />
                <div className="content-area">
                    {tab === INDICATOR_TAB_VALUES[0] && indicator?.status === ApiStatus.SUCCESS && (
                        <IndicatorDetails
                            data={indicatorModel}
                            fieldErrors={fieldErrors}
                            sourceList={sourceList?.data || []}
                            dataCount={dataCount}
                            isOverride={isOverride}
                            isShowUpdateTypeAlert={isShowUpdateTypeAlert}
                            sourceSearchValue={sourceSearchValue}
                            isSetUpIndicatorSourceDisabled={isSetUpDisabled}
                            updateFieldErrors={updateFieldErrors}
                            setOverride={setOverride}
                            setShowUpdateTypeAlert={setShowUpdateTypeAlert}
                            onChange={handleChange}
                            onSourceSearchChange={handleSourceSearchChange}
                            onSourceSelection={handleSourceSelection}
                        />
                    )}
                    {tab === INDICATOR_TAB_VALUES[1] && (
                        <IndicatorCountryList
                            data={indicatorCountryData || []}
                            fieldErrors={fieldErrors}
                            onChange={handleIndicatorValueChange}
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter className="indicator-edit-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    className="indicator-edit-button"
                    onClick={handleSave}
                    disabled={fieldErrors.length > 0 || (isShowUpdateTypeAlert && !isOverride)}>
                    {ACTION_ITEMS.UPDATE_BUTTON}
                </Button>
                <Button
                    variant={ButtonVariant.Secondary}
                    className="indicator-edit-button"
                    onClick={onClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditIndicator;
